/**
 * Store Write Guard — defense-in-depth protection against component data wipe.
 *
 * When a keyed component store is loaded with real backend data (via prefill
 * or REST fallback), this module:
 *
 * 1. Captures a snapshot of the loaded template data (`_prefillSnapshot`)
 * 2. Replaces `initialiseEmpty` / `resetStore` on the store so they cannot
 *    wipe a loaded store back to empty defaults
 * 3. Provides `validateSavePayload` — the final gate before any HTTP POST —
 *    which compares outbound data against the snapshot and refuses to send
 *    a payload that looks like a regression (empty, shrunken, or all-default)
 *
 * This module exists solely to make it IMPOSSIBLE for the inline editing path
 * to accidentally overwrite backend component config with empty data.
 */

// ─── 1. Protect a loaded store ─────────────────────────────────────────

/**
 * Call this AFTER a store has been populated with real data.
 * Captures a prefill snapshot and disables `initialiseEmpty` / `resetStore`
 * so legacy template code can't nuke the keyed store.
 *
 * @param {object} store — Zustand store (the return of getComponentStore)
 */
export function protectLoadedStore(store) {
  const state = store.getState();

  // Build a snapshot of the meaningful template data.
  // Most stores use `data` or `template_data`. Stores like API ResReq spread
  // config across requestData/sampleData/responseData — use getTemplate() if available.
  let dataBlob = state.data;
  if ((!dataBlob || (typeof dataBlob === 'object' && Object.keys(dataBlob).length <= 1)) && typeof state.getTemplate === 'function') {
    const t = state.getTemplate();
    dataBlob = t.data || t.componentData || t;
  }
  const snapshotStr = dataBlob ? JSON.stringify(dataBlob) : null;

  // Save the snapshot on the store state so saveComponentByType can read it.
  const guardPatch = { _loadProtected: true };
  if (snapshotStr && snapshotStr.length > 2) {
    // Only snapshot non-trivial data (not "{}" or "[]")
    guardPatch._prefillSnapshot = snapshotStr;
  }

  // Replace initialiseEmpty and resetStore with guarded versions that
  // refuse to wipe a loaded store.  Legacy templates call these on mount
  // (e.g. EmailWriter useEffect(() => store.getState().initialiseEmpty(), []))
  // which would destroy the keyed store data that inline editing relies on.
  const origInit = state.initialiseEmpty;
  const origReset = state.resetStore;

  if (typeof origInit === 'function') {
    guardPatch.initialiseEmpty = () => {
      if (store.getState()._loadProtected) {
        // Silently refuse — this is expected when a legacy template mounts
        // on a store that was already prefilled.
        return;
      }
      origInit();
    };
  }

  if (typeof origReset === 'function') {
    guardPatch.resetStore = () => {
      if (store.getState()._loadProtected) {
        return;
      }
      origReset();
    };
  }

  store.setState(guardPatch);
}

// ─── 2. Validate a save payload before it leaves the client ────────────

/**
 * Returns true if templateData looks like real, non-empty config.
 * Returns false (and logs an error) if it looks like the store was
 * uninitialised or wiped — in which case the save MUST be blocked.
 *
 * @param {string} componentType
 * @param {string} componentId
 * @param {object|Array} templateData — the data from getTemplate().data
 * @param {object} store — the keyed Zustand store
 * @returns {boolean} true = safe to save, false = BLOCK
 */
export function validateSavePayload(componentType, componentId, templateData, store) {
  // ── Gate 1: templateData must exist ──────────────────────────────────
  if (templateData == null) {
    console.error(`[SAVE GUARD] BLOCKED ${componentType}/${componentId}: templateData is null/undefined`);
    return false;
  }

  // ── Gate 2: templateData must not be empty ───────────────────────────
  if (typeof templateData === 'object' && !Array.isArray(templateData)) {
    if (Object.keys(templateData).length === 0) {
      console.error(`[SAVE GUARD] BLOCKED ${componentType}/${componentId}: templateData is empty object {}`);
      return false;
    }
  }
  if (Array.isArray(templateData) && templateData.length === 0) {
    console.error(`[SAVE GUARD] BLOCKED ${componentType}/${componentId}: templateData is empty array []`);
    return false;
  }

  // ── Gate 3: loaded flag must be true ─────────────────────────────────
  const state = store.getState();
  if (!state.loaded) {
    console.error(`[SAVE GUARD] BLOCKED ${componentType}/${componentId}: store.loaded is false`);
    return false;
  }

  // ── Gate 4: compare against prefill snapshot (regression detection) ──
  const snapshot = state._prefillSnapshot;
  if (snapshot) {
    const currentStr = JSON.stringify(templateData);
    const snapshotSize = snapshot.length;
    const currentSize = currentStr.length;

    // If the new payload is less than 1% the size of what was loaded from
    // the backend, something has gone very wrong — data was likely wiped.
    // The threshold is intentionally low because legitimate user actions
    // (e.g. switching merge-workflow mode) can shrink data significantly.
    if (snapshotSize > 50 && currentSize < snapshotSize * 0.01) {
      console.error(
        `[SAVE GUARD] BLOCKED ${componentType}/${componentId}: payload shrank from ${snapshotSize} to ${currentSize} bytes (${Math.round(currentSize / snapshotSize * 100)}%). Likely data wipe.`
      );
      return false;
    }
  }

  // ── Gate 5: spot-check for known empty/default patterns ──────────────
  if (typeof templateData === 'object' && !Array.isArray(templateData)) {
    const values = Object.values(templateData);
    const nonTrivialValues = values.filter(v => {
      if (v === '' || v === null || v === undefined || v === false || v === 0) return false;
      if (Array.isArray(v) && v.length === 0) return false;
      if (typeof v === 'object' && v !== null && Object.keys(v).length === 0) return false;
      return true;
    });

    // If the snapshot had real data but now EVERY value is empty/default,
    // this is almost certainly a wipe.
    if (snapshot && snapshot.length > 50 && nonTrivialValues.length === 0) {
      console.error(
        `[SAVE GUARD] BLOCKED ${componentType}/${componentId}: all templateData values are empty/default but snapshot had real data`
      );
      return false;
    }
  }

  return true;
}

// ─── 3. Remove protection (for intentional resets, e.g. discard) ───────

/**
 * Removes the load protection so `initialiseEmpty` works again.
 * Only call this for intentional user actions like "discard changes".
 */
export function unprotectStore(store) {
  store.setState({
    _loadProtected: false,
    _prefillSnapshot: null,
  });
}
